import React, { useEffect, useState } from 'react';
import {
  Popover, Box, Typography, TextField, ToggleButtonGroup, ToggleButton, Button, Stack, FormHelperText,
} from '@mui/material';
import axios from '../../config/axiosInstance';
import { useSnackbar } from 'notistack';
import { WEEKDAYS, fmtDate, errorMessage } from '../configuration/staffing/staffingUtils';

// Ajuste rápido de la dotación de un turno desde el pie del calendario: solo ese día
// (excepción puntual) o todos los mismos días de la semana. Guarda la regla y recalcula
// los calendarios abiertos afectados; los cerrados no se tocan.
export default function StaffingQuickEdit({ edit, stationId, workareaId, onClose, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const [total, setTotal] = useState('');
  const [scope, setScope] = useState('day');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (edit) {
      setTotal(String(edit.expected ?? 0));
      setScope('day');
    }
  }, [edit]);

  if (!edit) return null;

  const [year, month, dayOfMonth] = edit.day.split('-').map(Number);
  const weekday = (new Date(year, month - 1, dayOfMonth).getDay() + 6) % 7; // 0 = lunes
  const weekdayInfo = WEEKDAYS[weekday];

  const submit = async (value) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`/station/${stationId}/staffing/day`, {
        workarea_id: workareaId,
        workshift_id: Number(edit.turno.id),
        date: edit.day,
        total: value,
        scope,
      });
      if (data.recalc_failed) {
        enqueueSnackbar(
          'La dotación se guardó pero no se pudo aplicar al calendario. Usá "Aplicar a calendarios abiertos" en Gestión de dotaciones.',
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar('Dotación actualizada.', { variant: 'success' });
      }
      onClose();
      await onSaved?.();
    } catch (e) {
      enqueueSnackbar(errorMessage(e, 'No se pudo guardar la dotación.'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover
      open
      anchorEl={edit.anchorEl}
      onClose={saving ? undefined : onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Box sx={{ p: 2, width: 330 }}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.3 }}>
          Dotación · {edit.turno.name}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {weekdayInfo.long} {fmtDate(edit.day)} · hoy {edit.actual}/{edit.expected}
        </Typography>

        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          <TextField
            type="number" size="small" label="Cantidad requerida" value={total} autoFocus fullWidth
            onChange={(e) => setTotal(e.target.value.replace(/[^\d]/g, ''))}
            onKeyDown={(e) => { if (e.key === 'Enter' && total !== '') submit(Number(total)); }}
            inputProps={{ min: 0, style: { fontSize: '0.9rem' } }}
          />

          <Box>
            <ToggleButtonGroup
              exclusive size="small" fullWidth value={scope} onChange={(_e, v) => v && setScope(v)}
              sx={{ '& .MuiToggleButton-root': { flex: 1, py: 0.6, fontSize: '0.75rem', lineHeight: 1.2, textTransform: 'none', whiteSpace: 'nowrap' } }}
            >
              <ToggleButton value="day">Solo este día</ToggleButton>
              <ToggleButton value="weekday">Todos los {weekdayInfo.plural}</ToggleButton>
            </ToggleButtonGroup>
            <FormHelperText sx={{ mx: 0, minHeight: 32, fontSize: '0.7rem' }}>
              {scope === 'weekday' ? 'Desde este día en adelante, en los calendarios abiertos.' : ''}
            </FormHelperText>
          </Box>

          <Stack
            direction="row" justifyContent="space-between" alignItems="center"
            sx={{ '& .MuiButton-root': { fontSize: '0.72rem', whiteSpace: 'nowrap', minWidth: 0, px: 1.25, py: 0.5 } }}
          >
            <Button size="small" color="inherit" disabled={saving} onClick={() => submit(null)}>
              Quitar ajuste
            </Button>
            <Stack direction="row" spacing={0.5}>
              <Button size="small" onClick={onClose} disabled={saving}>Cancelar</Button>
              <Button size="small" variant="contained" disabled={saving || total === ''} onClick={() => submit(Number(total))}>
                Guardar
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Popover>
  );
}
